<template>
  <!-- Rendered in place, on the menu. Only the iOS overlay teleports. -->
  <button
    v-if="showButton"
    type="button"
    class="install-trigger"
    title="Add No Thanks! to your home screen"
    @click="onTriggerClick"
  >Add to Home Screen</button>

  <Teleport to="body">

    <div
      v-if="showIosOverlay"
      class="install-backdrop"
      @click.self="showIosOverlay = false"
    >
      <div class="install-panel" role="dialog" aria-label="Add to Home Screen">
        <button
          type="button"
          class="install-panel__close"
          aria-label="Close"
          @click="showIosOverlay = false"
        >&times;</button>
        <h2>Add to Home Screen</h2>
        <ol>
          <li>Tap <strong>Share</strong> in Safari's toolbar</li>
          <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
          <li>Tap <strong>Add</strong></li>
        </ol>
        <p class="install-panel__note">
          Safari only — iOS doesn't let Chrome or other browsers do this. An
          Apple restriction, not something this site controls.
        </p>
      </div>
    </div>
  </Teleport>
</template>

<script>
// Ported from the thunderstone / space-base repos' own InstallPrompt.vue, and
// deliberately behaviour-identical to them: the hub's game cards all link to
// `?install=1`, and the hub's CLAUDE.md says if that flow changes it changes
// in all of them. This game shipping without it would have made its hub card
// the only one whose Install link did nothing.
//
// iOS Safari has no JS API to trigger "Add to Home Screen" at all - Apple
// keeps it a manual Share-sheet action - so the closest a page can get is to
// capture Android/Chrome's real beforeinstallprompt for a genuine one-tap
// install, and on iOS show exactly which taps to make instead.
export default {
  name: 'InstallPrompt',
  data() {
    return {
      deferredPrompt: null,
      showIosOverlay: false,
      isStandalone: false,
      isIos: false,
    };
  },
  computed: {
    showButton() {
      return !this.isStandalone && (this.deferredPrompt || this.isIos);
    },
  },
  mounted() {
    // navigator.standalone is iOS Safari's own non-standard flag for "already
    // launched from a home-screen icon"; matchMedia covers everything else.
    // Someone who already installed this doesn't need to be offered it again.
    // Optional-chained on purpose: this component is mounted on the menu
    // screen now (see the style block), so it renders inside tests that never
    // needed a matchMedia stub before. A missing matchMedia should mean "not
    // installed", not a thrown error that takes the whole screen down.
    this.isStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches === true
      || window.navigator.standalone === true;
    this.isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // Set by the hub's game-card "Install" link - skips straight to the real
    // flow instead of landing someone here to hunt for a second button.
    const wantsAutoInstall = new URLSearchParams(window.location.search).get('install') === '1';

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      if (wantsAutoInstall) this.promptInstall();
    });

    if (wantsAutoInstall && this.isIos && !this.isStandalone) {
      this.showIosOverlay = true;
    }
  },
  methods: {
    onTriggerClick() {
      if (this.deferredPrompt) this.promptInstall();
      else if (this.isIos) this.showIosOverlay = true;
    },
    async promptInstall() {
      if (!this.deferredPrompt) return;
      this.deferredPrompt.prompt();
      await this.deferredPrompt.userChoice;
      this.deferredPrompt = null;
    },
  },
};
</script>

<style scoped>

/* Quiet, and on the menu only.
 *
 * Matt, 2026-08-27: "I don't think that I like how prominent the install
 * buttons are... it's not a main use case", and "the install button should
 * only appear on the hub for each game, and make sure it's not interfering
 * with anything else."
 *
 * It used to be a solid pill fixed to the top-right corner of EVERY screen,
 * including mid-game, where it sat over the board competing with the only two
 * buttons that matter. Now it renders in place on the menu, styled like the
 * "Around Table Round" link beside it - findable if you want it, never a
 * demand. Installing is something you do once, if ever.
 */
.install-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  gap: 6px;
  min-height: 40px;
  padding: 0.4rem 0.6rem;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.8rem;
  color: var(--ink-dim);
  opacity: 0.75;
  cursor: pointer;
  text-decoration: none;
}

.install-trigger:hover,
.install-trigger:focus-visible {
  opacity: 1;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.install-backdrop {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(0, 0, 0, 0.65);
  display: grid;
  place-items: center;
  padding: 1.25rem;
}

.install-panel {
  position: relative;
  max-width: 380px;
  padding: 1.25rem 1.4rem;
  border-radius: 14px;
  background: #22312a;
  border: 1px solid #3a4d43;
  color: #f2f5f3;
}

.install-panel h2 { margin: 0 0 0.7rem; font-size: 1.15rem; }
.install-panel ol { margin: 0; padding-left: 1.2rem; line-height: 1.7; }
.install-panel__note {
  margin: 0.9rem 0 0;
  font-size: 0.78rem;
  color: #b9c6bf;
}
.install-panel__close {
  position: absolute;
  top: 0.3rem;
  right: 0.5rem;
  min-width: 40px;
  min-height: 40px;
  font-size: 1.4rem;
  line-height: 1;
  border: none;
  background: none;
  color: #b9c6bf;
  cursor: pointer;
}
</style>
